import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
// Copyright   : 2023 semantics GmbH
// Date        : $Date: 2026-03-13 15:47:48 +0100 (Fri, 13 Mar 2026) $
// Author      : Dirk Rothe
// Worker      : $Author: d.rothe $
// Revision    : $Rev: 161511 $
// Purpose     : llm integration
// =============================================================================
// ---Third-party imports
import React from 'react';
import now from 'performance-now';
import clsx from 'clsx';
import { Button, Icon, Item, Message, List, Loader, Header, Segment } from 'semantic-ui-react';
// ---Framework imports
import log from 'uix/logging';
import { mapItems, strip, sum, callIfFunc, sorted, reversed, history, dict, keys, isString, isEqual, size, rstrip } from 'ldash';
import { Actions } from 'uix/registries';
import { Markdown, Highlight } from 'uix/chunks';
import { AutoSizer, XMenu, DelayedDisplay, Toolbar } from 'uix/components';
import { useRef, useResettableState, usePersistent, useEffect, useMemo, useState, useCallback, useApi } from 'uix/hooks';
import { usePersistentToggle } from 'uix/components/menu';
// ---Local imports
import styles from './chat.css';
import { usePersistentChat, useContextToggles, useDelayedText } from './hooks';
import { LUCKY } from './consts';
// ---Exports
const DEFAULT_MODEL = {
    name: 'gpt-4o-mini',
    maxInputTokens: 128000
};
const ModelSelector = ({ model, setModel, capability })=>{
    let { data } = useApi('lab/getActiveLLMs', {}, {
        cached: true
    });
    let models = useMemo(()=>{
        let models = data?.models ?? [];
        if (capability) {
            models = models.filter((m)=>m.capabilities[capability]);
        }
        return models;
    }, [
        data,
        capability
    ]);
    // switch to first if current model name is invalid
    useEffect(()=>{
        if (models.filter((m)=>m.name == model.name).length == 0 && models.length > 0) {
            setModel(models[0]);
        }
    }, [
        models
    ]);
    return /*#__PURE__*/ _jsx(XMenu.Dropdown, {
        options: models.map((m)=>({
                value: m.name,
                key: m.name,
                text: m.name
            })),
        value: model.name,
        onValueChange: (v)=>setModel(models.filter((m)=>m.name == v)[0])
    });
};
export const useModelSelector = ({ capability })=>{
    let [model, setModel] = usePersistent('model', DEFAULT_MODEL);
    let modelSelector = /*#__PURE__*/ _jsx(ModelSelector, {
        setModel: setModel,
        model: model,
        capability: capability
    });
    return [
        model,
        modelSelector
    ];
};
const estimateTokens = (text)=>{
    /* Estimates the number of tokens in a given text.
   This function provides a rough estimate of the number of tokens
   in a text string.  It's based on word count, character count, and punctuation.
  */ const additionalTokens = (text.match(/[\s.,!?;]/g) || []).length;
    return Math.round((text.split(' ').length / 0.75 + text.length / 4 + 2 * additionalTokens) / 2);
};
const PreText = ({ text })=>/*#__PURE__*/ _jsx("pre", {
        style: {
            margin: '0px',
            textWrap: 'balance',
            lineHeight: '1.0em',
            fontFamily: 'Arial, sans-serif'
        },
        children: text
    });
const PauseElement = ({ unpause, remainingPauseTime })=>{
    const [timeLeft, setTimeLeft] = useState(remainingPauseTime);
    useEffect(()=>{
        // Update the timeLeft every 100ms
        const interval = setInterval(()=>{
            setTimeLeft((prevTime)=>{
                const newTime = prevTime - 100;
                return newTime >= 0 ? newTime : 0;
            });
        }, 100);
        // Clear the interval when the component unmounts
        return ()=>clearInterval(interval);
    }, [
        remainingPauseTime
    ]);
    // Calculate the percentage for the progress bar
    const percentage = remainingPauseTime > 0 ? timeLeft / remainingPauseTime * 100 : 0;
    return /*#__PURE__*/ _jsxs("span", {
        onClick: unpause,
        style: {
            display: 'inline-flex',
            alignItems: 'center',
            cursor: 'pointer',
            marginLeft: '10px'
        },
        children: [
            Math.ceil((timeLeft || 0) / 1000),
            "s ",
            /*#__PURE__*/ _jsx("div", {
                style: {
                    position: 'relative',
                    width: '60px',
                    height: '6px',
                    backgroundColor: '#ddd',
                    borderRadius: '3px',
                    overflow: 'hidden'
                },
                children: /*#__PURE__*/ _jsx("div", {
                    style: {
                        width: `${percentage}%`,
                        height: '100%',
                        backgroundColor: '#76c7c0',
                        transition: 'width 0.1s linear'
                    }
                })
            })
        ]
    });
};
let resolveFunctions = (text, function_calls)=>{
    if (size(function_calls)) {
        let whitelist = function_calls.map((f)=>f.name);
        const startDelimiter = '\n```json\n';
        const endDelimiter = '\n```\n';
        const funcNamesPattern = whitelist.join('|');
        //~ const regex = new RegExp(`(${funcNamesPattern})\\s*(\\{[^]*?\\})`, 'g')
        //~ return text.replace(regex, `${startDelimiter} $1 $2 ${endDelimiter}`)
        // Regular expression to split the text into code and non-code segments
        const splitter = /(```[\s\S]*?```|`[^`]*`)/g;
        const segments = text.split(splitter);
        const processedSegments = segments.map((segment)=>{
            // Check if the segment is code (inline or block)
            if (/^(```[\s\S]*?```|`[^`]*`)$/.test(segment)) {
                // Return code segments unmodified
                return segment;
            } else {
                // Process non-code segments
                const regex = new RegExp(`(${funcNamesPattern})\\s*(\\{[^]*?\\})`, 'g');
                return segment.replace(regex, `${startDelimiter} $1 $2 ${endDelimiter}`);
            }
        });
        text = processedSegments.join('');
    }
    return text;
};
const MessageOutput = ({ msg, onFunctionCallClick, onMarkdownLinkClick, pauseOnMarkdownLink, debug, wordsPerSec })=>{
    /* render with markdown-to-jsx
   */ if (wordsPerSec == 0 && pauseOnMarkdownLink) {
        // pauseOnMarkdownLink needs at least some slowdown (0 will pass through full chunks)
        wordsPerSec = 6;
    }
    let { text, pauses } = useDelayedText({
        text: msg.content,
        enabled: !!msg.meta?.streaming,
        wordsPerSec,
        pauseOnMarkdownLink
    });
    if (pauses.activePause) {
        text = rstrip(text) + ' <Pause>';
    }
    useEffect(()=>{
        if (pauses.activePause?.target.href) {
            callIfFunc(onMarkdownLinkClick, {
                href: pauses.activePause.target.href
            });
        }
    }, [
        pauses.activePause
    ]);
    let buildPause = ()=>/*#__PURE__*/ _jsx(PauseElement, {
            unpause: pauses.unpause,
            remainingPauseTime: pauses.remainingPauseTime(now())
        });
    let funcResolvedText = resolveFunctions(text, msg.meta?.function_calls);
    // escape unescaped periods in numbered markdown link labels
    let escapedMarkdown = useMemo(()=>funcResolvedText.replace(/(\[)(\d+)\.(?!\\)/g, '$1$2\\.'), [
        funcResolvedText
    ]);
    let getFuncCallfromCode = (code)=>{
        code = strip(code);
        for (let prefix of [
            'json',
            'plaintext'
        ]){
            if (code.startsWith(prefix)) {
                code = strip(code.slice(prefix.length));
            }
        }
        let fcs = msg.meta?.function_calls || [];
        let fc = fcs.find(({ name, arguments: args })=>{
            if (code.startsWith(name)) {
                let parsed_args = JSON.parse(code.slice(name.length));
                return isEqual(parsed_args, args);
            }
        });
        return fc;
    };
    let onCodeClick = (code)=>{
        /* for embedded function calls in messages
    match code in chat messages to collected function calls
    */ if (isString(code)) {
            let fc = getFuncCallfromCode(code);
            if (fc) {
                callIfFunc(onFunctionCallClick, fc);
            } else {
                log.warn('did not find code in function_calls');
            }
        } else {
            log.warn('code is no raw string');
        }
    };
    let buildCode = (props)=>{
        let onClick = undefined;
        if (size(msg.meta?.function_calls) && isString(props.children) && getFuncCallfromCode(props.children)) {
            onClick = onCodeClick;
        }
        return /*#__PURE__*/ _jsx(HighlightedCode, {
            onCodeClick: onClick,
            ...props
        });
    };
    let _onMarkdownLinkClick = (event, href)=>{
        if (!href?.startsWith('http')) {
            event.preventDefault(); // Prevent the default redirect
            callIfFunc(onMarkdownLinkClick, {
                href
            });
        }
    };
    let buildLink = ({ children, href, ...props })=>{
        if (href?.startsWith('http')) {
            props = {
                ...props,
                target: '_mdlink'
            };
        }
        return /*#__PURE__*/ _jsx("a", {
            ...props,
            href: href,
            onClick: (event)=>_onMarkdownLinkClick(event, href),
            children: children
        });
    };
    let res = /*#__PURE__*/ _jsx(Markdown, {
        options: {
            overrides: {
                code: buildCode,
                a: buildLink,
                Pause: buildPause
            }
        },
        children: escapedMarkdown
    });
    // show raw output in debug mode
    if (debug) {
        res = /*#__PURE__*/ _jsx(PreText, {
            text: escapedMarkdown
        });
    }
    return res;
};
const MessageEditor = ({ msg, index, onClose, onSave })=>{
    // State to hold the editable content of the message
    const [editContent, setEditContent] = useState(msg.content);
    // Create a ref for the textarea to capture focus
    const textareaRef = useRef(null);
    // Handle change in the text area
    const handleChange = (e)=>{
        setEditContent(e.target.value);
        autoResizeTextarea(e.target);
    };
    // Automatically resize the textarea based on content
    const autoResizeTextarea = (textarea)=>{
        textarea.style.height = 'auto'; // Reset the height
        textarea.style.height = `${textarea.scrollHeight}px`; // Set it to the scrollHeight
    };
    // Handle save action
    const handleSave = (execute)=>{
        callIfFunc(onSave, index, editContent, execute);
        onClose(); // Close the editor after saving
    };
    // Handle cancel action: simply close the editor without saving changes
    const handleCancel = ()=>{
        onClose();
    };
    // Handle key down events for handling submit and newline
    const handleTextareaKeyDown = (e)=>{
        if (e.key === 'Enter') {
            if (e.shiftKey) {
            // Allow the default behavior of adding a newline
            } else if (e.ctrlKey) {
                e.preventDefault();
                handleSave(true);
            } else {
                e.preventDefault();
                handleSave(false);
            }
        }
    };
    // Add event listener for Escape key press
    useEffect(()=>{
        const handleKeyDown = (e)=>{
            if (e.key === 'Escape') {
                onClose();
            }
        };
        // Add the keydown event listener when the component mounts
        document.addEventListener('keydown', handleKeyDown);
        // Cleanup: remove the event listener when the component unmounts
        return ()=>{
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [
        onClose
    ]); // Dependency array contains onClose to ensure freshness
    // Capture focus on the textarea when the component mounts
    useEffect(()=>{
        if (textareaRef.current) {
            const textarea = textareaRef.current;
            textarea.focus();
            textarea.selectionStart = textarea.selectionEnd = textarea.value.length; // Move cursor to end
            autoResizeTextarea(textarea);
        }
    }, []); // Empty dependency array to run on component mount
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.messageEditor,
        children: [
            /*#__PURE__*/ _jsx("textarea", {
                ref: textareaRef,
                value: editContent,
                onChange: handleChange,
                onKeyDown: handleTextareaKeyDown,
                style: {
                    width: '100%',
                    padding: '10px',
                    paddingBottom: '0px',
                    fontFamily: 'Arial, sans-serif',
                    border: '1px solid #ccc',
                    borderRadius: '0px',
                    overflow: 'hidden',
                    resize: 'none'
                },
                onBlur: onClose
            }),
            /*#__PURE__*/ _jsxs("div", {
                style: {
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: '10px'
                },
                children: [
                    /*#__PURE__*/ _jsxs("span", {
                        style: {
                            fontSize: '12px',
                            color: '#666'
                        },
                        children: [
                            /*#__PURE__*/ _jsx("strong", {
                                children: "Enter"
                            }),
                            " (save) ",
                            /*#__PURE__*/ _jsx("strong", {
                                children: "+Shift"
                            }),
                            " (new line) ",
                            /*#__PURE__*/ _jsx("strong", {
                                children: "+Ctrl"
                            }),
                            ' ',
                            "(run) ",
                            /*#__PURE__*/ _jsx("strong", {
                                children: "Escape"
                            }),
                            " (close)"
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                onClick: handleCancel,
                                compact: true,
                                basic: true,
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                onClick: ()=>handleSave(false),
                                compact: true,
                                secondary: true,
                                children: "Save"
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
const SystemMessageItem = ({ returnedSystemMessages, rawSystemMessages, systemMessageEditor, model })=>{
    // The markdown parser may misinterpret numbers followed by periods
    // as the start of ordered lists or nested lists, leading to incorrect HTML output.
    // To prevent this, use a regular expression to escape the periods after
    // numbers in the list items.
    let msgs = returnedSystemMessages;
    if (rawSystemMessages) {
        msgs = rawSystemMessages.map((m)=>({
                content: m
            }));
    }
    let escape = (md)=>md.replace(/(-\s.*?)(\d+)\.(?!\\)/g, '$1$2\\.');
    let [sysmsgMarkdown, sysmsgMarkdown_toggle] = usePersistentToggle('sysmsgMarkdown', {
        content: 'Markdown',
        icon: 'eye'
    });
    let menu = /*#__PURE__*/ _jsxs(XMenu, {
        size: "tiny",
        borderless: true,
        padded: true,
        children: [
            systemMessageEditor,
            sysmsgMarkdown_toggle
        ]
    });
    let items = msgs.map((msg, i)=>/*#__PURE__*/ _jsxs(Segment, {
            attached: true,
            children: [
                /*#__PURE__*/ _jsxs(Header, {
                    dividing: true,
                    textAlign: "center",
                    sub: true,
                    size: "huge",
                    children: [
                        msg.role || 'system',
                        " message (",
                        estimateTokens(msg.content),
                        " tokens)"
                    ]
                }),
                sysmsgMarkdown ? /*#__PURE__*/ _jsx(Markdown, {
                    children: escape(msg.content)
                }) : /*#__PURE__*/ _jsx(PreText, {
                    text: msg.content
                })
            ]
        }, i));
    let num_tokens = useMemo(()=>{
        return sum(msgs.map((msg)=>estimateTokens(msg.content || '')));
    }, [
        msgs
    ]);
    let used = num_tokens / model.maxInputTokens * 100;
    let cornerContent = /*#__PURE__*/ _jsxs("span", {
        title: `${num_tokens} of ${model.maxInputTokens}`,
        style: {
            color: used > 80 ? 'red' : undefined
        },
        children: [
            used.toFixed(0),
            "%"
        ]
    });
    return /*#__PURE__*/ _jsx(XMenu.DialogItem, {
        content: "Regeln",
        title: "System messages used during last completion",
        cornerContent: cornerContent,
        dlgHeader: menu,
        name: "sysmsgs",
        children: /*#__PURE__*/ _jsx("div", {
            style: {
                height: '100%',
                overflow: 'auto'
            },
            children: items
        })
    });
};
const History = ({ historyctrl })=>{
    let { history, loadThreadFromHistory, setHistory, clearHistory, pinThreadInHistory } = historyctrl;
    let entries = reversed(sorted(history, (h)=>h.dt)).map(({ thread_id, dt, messages, pinned })=>/*#__PURE__*/ _jsxs(List.Item, {
            children: [
                /*#__PURE__*/ _jsxs(List.Content, {
                    floated: "right",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            size: "tiny",
                            compact: true,
                            basic: true,
                            icon: "trash",
                            onClick: ()=>setHistory(history.filter((h)=>h.thread_id !== thread_id))
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            size: "tiny",
                            compact: true,
                            basic: true,
                            icon: "pin",
                            active: pinned,
                            onClick: ()=>pinThreadInHistory(thread_id, !pinned)
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(List.Header, {
                    as: "a",
                    onClick: ()=>loadThreadFromHistory(thread_id),
                    children: messages?.[0].content
                }),
                /*#__PURE__*/ _jsx(List.Description, {
                    children: dt
                })
            ]
        }, thread_id));
    return /*#__PURE__*/ _jsxs("div", {
        style: {
            minWidth: '200px',
            maxHeight: '70vh',
            overflow: 'auto'
        },
        children: [
            /*#__PURE__*/ _jsx(List, {
                divided: true,
                relaxed: true,
                children: entries
            }),
            /*#__PURE__*/ _jsx(Button, {
                basic: true,
                compact: true,
                floated: "right",
                onClick: ()=>clearHistory(),
                content: "clear"
            })
        ]
    });
};
const ShowFunctions = ({ functions })=>{
    let funcs = functions?.map((f)=>{
        let args = mapItems(f.parameters.properties, (param_name, { type, description })=>`## ${param_name} (${type})\n${description}\n`).join('\n');
        return `# ${f.name}\n${f.description}\n${args}\n`;
    }).join('\n\n');
    //~ return <pre>{funcs}</pre>
    return /*#__PURE__*/ _jsx(Markdown, {
        children: funcs || '...'
    });
};
const HighlightedCode = ({ className, onCodeClick, children })=>{
    let onClick = ()=>{
        callIfFunc(onCodeClick, children);
    };
    let cursorStyle = {
        cursor: onCodeClick ? 'pointer' : undefined
    };
    if (className?.startsWith('lang-')) {
        return /*#__PURE__*/ _jsx(Highlight, {
            code: children,
            language: className.slice(5),
            children: ({ style, tokens, getLineProps, getTokenProps })=>/*#__PURE__*/ _jsx("code", {
                    style: {
                        ...style,
                        ...cursorStyle
                    },
                    className: styles.highlight,
                    onClick: onClick,
                    children: tokens.map((line, i)=>/*#__PURE__*/ _jsx("div", {
                            ...getLineProps({
                                line
                            }),
                            children: line.map((token, key)=>/*#__PURE__*/ _jsx("span", {
                                    ...getTokenProps({
                                        token
                                    })
                                }, key))
                        }, i))
                })
        });
    }
    return /*#__PURE__*/ _jsx("code", {
        onClick: onClick,
        style: cursorStyle,
        children: children
    });
};
const MessageInteraction = ({ messages, index, interact, hovered, onSetMessageEdit })=>{
    let res = null;
    if (messages[index]['role'] == 'user') {
        res = /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                /*#__PURE__*/ _jsx(Button, {
                    icon: "refresh",
                    size: "tiny",
                    compact: true,
                    secondary: true,
                    floated: "right",
                    onClick: ()=>interact.retryFromUserMessage(index),
                    style: {
                        opacity: hovered ? undefined : 0.1,
                        transition: 'all 1s'
                    },
                    title: "retry"
                }),
                /*#__PURE__*/ _jsx(Button, {
                    icon: "edit",
                    size: "tiny",
                    compact: true,
                    secondary: true,
                    floated: "right",
                    onClick: ()=>onSetMessageEdit(index),
                    style: {
                        opacity: hovered ? undefined : 0.1,
                        transition: 'all 1s'
                    },
                    title: "retry"
                })
            ]
        });
    }
    return res;
};
const Chat = ({ messages, contextRules, onEdited, onFunctionCallClick, interact, editLastMessage, onMarkdownLinkClick, pauseOnMarkdownLink, debug, wordsPerSec })=>{
    // track hovered message index
    let [hovered, setHovered] = useState(null);
    let [messageEdit, setMessageEdit] = useState(null);
    // go into editing mode changed externally
    useEffect(()=>{
        if (editLastMessage !== null) {
            // last is llm output, so edit the user message befor
            setMessageEdit(messages.length - 2);
        }
    }, [
        editLastMessage
    ]);
    let icon = (msg)=>{
        let name = 'server';
        if (msg.role == 'user') name = 'user';
        // else role assistant
        if (msg.meta?.function_calls) {
            // custom case: recovered function_calls by scanning text
            // here we have a combination of a natural language response plus parsed function calls
            return /*#__PURE__*/ _jsxs(Icon.Group, {
                className: styles.iconGroup,
                children: [
                    /*#__PURE__*/ _jsx(Icon, {
                        name: name
                    }),
                    /*#__PURE__*/ _jsx(Icon, {
                        name: "cog",
                        corner: "bottom right"
                    })
                ]
            });
        } else {
            return /*#__PURE__*/ _jsx(Icon, {
                name: name,
                className: styles.singleIcon
            });
        }
    };
    let renderedMessageOutputs = useMemo(()=>messages.map((msg)=>/*#__PURE__*/ _jsx(MessageOutput, {
                msg: msg,
                onFunctionCallClick: onFunctionCallClick,
                onMarkdownLinkClick: onMarkdownLinkClick,
                pauseOnMarkdownLink: pauseOnMarkdownLink,
                debug: debug,
                wordsPerSec: wordsPerSec
            })), [
        onMarkdownLinkClick,
        debug,
        onFunctionCallClick,
        messages,
        wordsPerSec
    ]);
    let renderMessage = (msg, i, isLast)=>{
        if (!msg.content && isLast && msg.meta?.streaming) {
            return /*#__PURE__*/ _jsx(DelayedDisplay, {
                delay: 2000,
                children: /*#__PURE__*/ _jsx(Loader, {
                    inline: true,
                    active: true,
                    indeterminate: true,
                    size: "mini",
                    style: {
                        marginTop: '-5px'
                    }
                })
            });
        }
        // default is markdown output
        let res = renderedMessageOutputs[i];
        // customize tool/function call
        //~ let isFuncCall = msg =>
        //~ ['function_call'].includes(msg.finish_reason) ||
        //~ (msg.content.startsWith(msg.meta?.function_calls?.[0]?.name) && msg.content.endsWith('}'))
        //~ let format_args = args => mapItems(args, (k, v) => `${k}='${v}'`).join(', ')
        //~ if (isFuncCall(msg)) {
        //~ if (msg.meta.function_calls.length > 0) {
        //~ let multiple = msg.meta.function_calls.length > 1
        //~ let s = msg.meta.function_calls.map((fc, i) => (
        //~ <code key={i} style={{display: multiple ? 'inline-block' : undefined}}>{`${
        //~ fc.name
        //~ }(${format_args(fc.arguments)})`}</code>
        //~ ))
        //~ res = s
        //~ }
        //~ } else
        if ([
            'remote_error',
            'exception',
            'bad_response'
        ].includes(msg.finish_reason)) {
            // XXX: where is msg.content?
            res = /*#__PURE__*/ _jsxs("code", {
                style: {
                    color: '#C22'
                },
                children: [
                    msg.finish_reason,
                    ": ",
                    msg.content
                ]
            });
        }
        return res;
    };
    // context rules can indicate loaded stuff in chat window
    let indicators = mapItems(contextRules, (name, r)=>{
        let ind = r.buildChatIndicator();
        if (ind) {
            return /*#__PURE__*/ _jsx("span", {
                children: r.buildChatIndicator()
            }, name);
        }
        return null;
    }, (n, r)=>r.buildChatIndicator);
    return /*#__PURE__*/ _jsxs("div", {
        className: styles.container,
        children: [
            messages.map((msg, index)=>{
                return /*#__PURE__*/ _jsxs("div", {
                    style: {
                        marginTop: '5px',
                        marginBottom: '5px'
                    },
                    onMouseEnter: ()=>setHovered(index),
                    onMouseLeave: ()=>setHovered(null),
                    children: [
                        /*#__PURE__*/ _jsx(MessageInteraction, {
                            hovered: hovered == index,
                            messages: messages,
                            index: index,
                            interact: interact,
                            onSetMessageEdit: setMessageEdit
                        }),
                        icon(msg),
                        messageEdit == index ? /*#__PURE__*/ _jsx(MessageEditor, {
                            msg: msg,
                            index: index,
                            onClose: ()=>setMessageEdit(null),
                            onSave: interact.editMessage
                        }) : /*#__PURE__*/ _jsx("div", {
                            className: styles.output,
                            children: renderMessage(msg, index, index == messages.length - 1)
                        })
                    ]
                }, index);
            }),
            messages.at(-1)?.meta?.streaming ? null : indicators
        ]
    });
};
const ChatScroller = ({ messages, ...props })=>{
    const containerRef = useRef(null);
    const isProgrammaticScroll = useRef(false);
    const [isAutoScrollActive, setIsAutoScrollActive] = useState(true);
    const onUserScroll = ()=>{
        if (isProgrammaticScroll.current) {
            isProgrammaticScroll.current = false;
            return;
        }
        const container = containerRef.current;
        // Check if the scroll is at the bottom
        const isAtBottom = Math.abs(container.scrollHeight - container.scrollTop - container.clientHeight) < 20;
        setIsAutoScrollActive(isAtBottom);
    };
    useEffect(()=>{
        const container = containerRef.current;
        if (container && isAutoScrollActive) {
            isProgrammaticScroll.current = true;
            container.scrollTop = container.scrollHeight;
        }
    }, [
        messages,
        isAutoScrollActive
    ]);
    return /*#__PURE__*/ _jsx("div", {
        ref: containerRef,
        style: {
            flex: 1,
            overflow: 'auto',
            marginBottom: '10px'
        },
        onScroll: onUserScroll,
        children: /*#__PURE__*/ _jsx(Chat, {
            messages: messages,
            ...props
        })
    });
};
const PromptInput = ({ onSetPrompt, onEditLastMessage, refocusOnPromptInput })=>{
    const ref = useRef(null);
    const [inputValue, setInputValue] = useState('');
    const handleInputChange = (e)=>{
        setInputValue(e.target.value);
        resizeTextarea();
    };
    const handleKeyDown = (e)=>{
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); // Prevent the default action to avoid a line break in the textarea
            if (strip(inputValue)) {
                onSetPrompt(inputValue); // Call the passed `onSetPrompt` function with the current input value
                setInputValue(''); // Optionally clear the input after sending
            }
        }
        // if cursor up pressed and no text available setEditLastMessage to true
        if (e.key === 'ArrowUp' && strip(inputValue) === '') {
            // set a random value to wake up via useEffect
            onEditLastMessage(Math.random());
        }
    };
    const resizeTextarea = ()=>{
        let textarea = ref.current;
        if (textarea) {
            textarea.style.height = 'auto'; // Reset height to recalculating
            textarea.style.height = `${textarea.scrollHeight}px`; // Set to scroll height
        }
    };
    useEffect(()=>{
        if (ref.current) {
            ref.current.focus();
        }
    }, [
        refocusOnPromptInput
    ]);
    return /*#__PURE__*/ _jsx("textarea", {
        ref: ref,
        value: inputValue,
        onChange: handleInputChange,
        onKeyDown: handleKeyDown,
        placeholder: "Type your message and hit Enter to send...",
        style: {
            maxHeight: '300px',
            height: 'auto',
            overflowY: 'hidden',
            flex: 1,
            padding: '10px',
            fontFamily: 'Arial, sans-serif',
            border: '1px solid #ccc',
            borderRadius: '5px',
            backgroundColor: '#f9f9f9',
            outline: 'none',
            transition: 'border-color 0.3s',
            resize: 'none'
        },
        onFocus: ()=>ref.current.style.borderColor = '#80bdff',
        onBlur: ()=>ref.current.style.borderColor = '#ccc'
    });
};
export const ChatPanel = ({ contextRules = {}, systemMessages, systemMessageEditor, functions, onFunctionCall, onMarkdownLinkClick, pauseOnMarkdownLink = false })=>{
    let [model, modelSelector] = useModelSelector({});
    let { menuItem: contextToggle, toggleState } = useContextToggles(contextRules);
    let [temperature, setTemperature] = usePersistent('temperature', 1, 4);
    let [wordsPerSec, setWordsPerSec] = usePersistent('wordsPerSec', 0, 1);
    let [debug, debug_toggle] = usePersistentToggle('debug_chat', {
        icon: 'bug'
    });
    let _onFunctionCall = useCallback((func_call)=>callIfFunc(onFunctionCall, func_call), [
        onFunctionCall
    ]);
    // build list of context names from toggle states
    let activeContextRules = useMemo(()=>dict(mapItems(contextRules, (name, r)=>[
                name,
                r
            ], (name)=>toggleState[name])), [
        toggleState,
        contextRules
    ]);
    let context = keys(activeContextRules);
    let [refocusOnPromptInput, setRefocusOnPromptInput] = useState(0);
    let { messages, reset, newPrompt, loading, rate_limits, debug_data, historyctrl, interact } = usePersistentChat({
        systemMessages,
        context,
        model: model.name,
        temperature: (temperature / 100).toFixed(3),
        onFunctionCall: _onFunctionCall,
        contextRules: activeContextRules,
        setRefocusOnPromptInput
    });
    useEffect(()=>{
        if (debug) vl.pprint(debug_data);
    }, [
        debug_data,
        debug
    ]);
    let total_price = sum(messages.filter((m)=>m.meta?.price).map((m)=>m.meta.price));
    let total_in_toks = Math.floor(sum(messages.filter((m)=>m.meta?.num_input_tokens).map((m)=>m.meta.num_input_tokens)));
    let last_in_toks = Math.floor(messages.at(-1)?.meta?.num_input_tokens);
    // dispatch edit command between PromptInput and ChatScroller
    let [editLastMessage, setEditLastMessage] = useResettableState(null, messages);
    return /*#__PURE__*/ _jsx(AutoSizer, {
        children: ({ height, width })=>/*#__PURE__*/ _jsxs("div", {
                style: {
                    height,
                    width,
                    display: 'flex',
                    flexDirection: 'column'
                },
                children: [
                    /*#__PURE__*/ _jsxs(Toolbar, {
                        colored: true,
                        attached: true,
                        size: "tiny",
                        children: [
                            /*#__PURE__*/ _jsx(XMenu.Item, {
                                icon: "trash",
                                onClick: reset
                            }),
                            contextToggle,
                            modelSelector,
                            /*#__PURE__*/ _jsx(XMenu.BeginRight, {}),
                            /*#__PURE__*/ _jsx(XMenu.Label, {
                                content: `${total_price.toFixed(total_price > 1 ? 1 : 2)}c`,
                                title: `sum input tokens: ${total_in_toks} last: ${last_in_toks} (${total_price.toFixed(5)}c)`
                            }),
                            /*#__PURE__*/ _jsx(SystemMessageItem, {
                                returnedSystemMessages: debug_data.sys_messages || [],
                                rawSystemMessages: systemMessages,
                                systemMessageEditor: systemMessageEditor,
                                model: model
                            }),
                            size(debug_data.functions) > 0 && /*#__PURE__*/ _jsx(XMenu.PopupItem, {
                                content: "F",
                                padded: true,
                                keepOpen: true,
                                title: "Functions declared during last completion",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: styles.functions_markdown,
                                    children: /*#__PURE__*/ _jsx(ShowFunctions, {
                                        functions: debug_data.functions
                                    })
                                })
                            }),
                            /*#__PURE__*/ _jsx(XMenu.PopupItem, {
                                content: "RL",
                                padded: true,
                                keepOpen: true,
                                title: "Rate Limits",
                                children: mapItems(rate_limits, (name, value)=>/*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            name,
                                            ": ",
                                            value
                                        ]
                                    }, name))
                            }),
                            /*#__PURE__*/ _jsx(XMenu.PopupItem, {
                                icon: "calendar",
                                wide: "very",
                                padded: true,
                                disabled: historyctrl.history.length == 0,
                                open: historyctrl.history.length == 0 ? false : undefined,
                                children: /*#__PURE__*/ _jsx(History, {
                                    historyctrl: historyctrl
                                })
                            }),
                            debug_toggle,
                            /*#__PURE__*/ _jsxs(XMenu.SubMenu, {
                                icon: "configure",
                                keepOpen: true,
                                children: [
                                    /*#__PURE__*/ _jsx(XMenu.Label, {
                                        content: "Temperature"
                                    }),
                                    /*#__PURE__*/ _jsx(XMenu.Slider, {
                                        value: temperature,
                                        min: 0,
                                        max: 100,
                                        onValueChange: setTemperature,
                                        title: "Temperature (response randomnes) (0 = auto)",
                                        valueLabeled: true
                                    }),
                                    /*#__PURE__*/ _jsx(XMenu.Label, {
                                        content: "Words/Sec"
                                    }),
                                    /*#__PURE__*/ _jsx(XMenu.Slider, {
                                        value: wordsPerSec,
                                        min: 0,
                                        max: 30,
                                        onValueChange: setWordsPerSec,
                                        title: "Speed of drawing new words (0 = disabled limit)",
                                        valueLabeled: true
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx(XMenu.Item, {
                                icon: "help",
                                title: "Help me",
                                onClick: ()=>newPrompt('Beschreibe deine Aufgabe und Funktion und zeige Beispiele.')
                            }),
                            /*#__PURE__*/ _jsx(XMenu.Item, {
                                content: LUCKY,
                                title: "I'm feeling lucky!",
                                onClick: ()=>newPrompt(LUCKY)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(ChatScroller, {
                        messages: messages,
                        onFunctionCallClick: _onFunctionCall,
                        contextRules: activeContextRules,
                        interact: interact,
                        editLastMessage: editLastMessage,
                        onMarkdownLinkClick: onMarkdownLinkClick,
                        pauseOnMarkdownLink: pauseOnMarkdownLink,
                        debug: debug,
                        wordsPerSec: wordsPerSec
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        style: {
                            display: 'flex'
                        },
                        children: /*#__PURE__*/ _jsx(PromptInput, {
                            onSetPrompt: newPrompt,
                            onEditLastMessage: setEditLastMessage,
                            refocusOnPromptInput: refocusOnPromptInput
                        })
                    })
                ]
            })
    });
};
